/**
 * Client-side defense for free-text form fields.
 * SQL injection is still prevented on the API with parameterized queries;
 * this strips characters that should not appear in user-entered text.
 *
 * Allowed special characters on name: # - / , . :
 * Allowed special characters on address: # - / , . : & ( )
 */
const UNSAFE_FORM_TEXT_SOURCE = "[<>'\"\\\\;`|&$={}[\\]\\x00]|--|/\\*|\\*/";

function unsafeFormTextPattern() {
  return new RegExp(UNSAFE_FORM_TEXT_SOURCE, 'g');
}

/** Letters, spaces, and: # - / , . : */
const PERSON_NAME_DISALLOWED = /[^A-Za-z #/,.:-]/g;
const PERSON_NAME_VALID = /^[A-Za-z #/,.:-]*$/;

/** Letters, digits, spaces, and: # - / , . : & ( ) */
const ADDRESS_DISALLOWED = /[^A-Za-z0-9 #/,.:&()-]/g;
const ADDRESS_VALID = /^[A-Za-z0-9 #/,.:&()-]*$/;

const ADDRESS_FIELD_RE = /address|adress/i;
const PERSON_NAME_FIELD_SUFFIXES = [
  'applicant_name',
  'full_name',
  'relative_name',
  'owner_name',
  'father_name',
  'guardian_name',
  'new_name',
  'old_name',
  'cp1_name',
  'cp2_name',
  'installer_name',
  'authority_name',
  'near_by_name',
  'consumer_name',
  'newname',
  'guardianname',
];

export const SAFE_FORM_TEXT_MESSAGE = 'Special characters are not allowed.';

export function sanitizeFormTextInput(value = '') {
  return String(value ?? '').replace(unsafeFormTextPattern(), '');
}

export function isSafeFormText(value = '') {
  return !unsafeFormTextPattern().test(String(value ?? ''));
}

export function sanitizePersonNameInput(value = '') {
  return String(value ?? '').replace(PERSON_NAME_DISALLOWED, '');
}

export function isSafePersonName(value = '') {
  return PERSON_NAME_VALID.test(String(value ?? ''));
}

export function sanitizeAddressInput(value = '') {
  return String(value ?? '').replace(ADDRESS_DISALLOWED, '');
}

export function isSafeAddress(value = '') {
  return ADDRESS_VALID.test(String(value ?? ''));
}

export function getFormFieldSanitizeMode(fieldName = '', explicitMode) {
  if (explicitMode === false) return null;
  if (explicitMode === 'name' || explicitMode === 'address' || explicitMode === 'text') {
    return explicitMode;
  }

  const name = String(fieldName || '').toLowerCase();
  if (!name) return 'text';
  if (ADDRESS_FIELD_RE.test(name)) return 'address';
  if (PERSON_NAME_FIELD_SUFFIXES.some((suffix) => name === suffix || name.endsWith(`_${suffix}`))) {
    return 'name';
  }
  return 'text';
}

export function sanitizeFormFieldInput(value = '', mode = 'text') {
  if (mode === 'name') return sanitizePersonNameInput(value);
  if (mode === 'address') return sanitizeAddressInput(value);
  return sanitizeFormTextInput(value);
}

function yupSafeValue(yupString, testName, isSafe) {
  return yupString.test(
    testName,
    SAFE_FORM_TEXT_MESSAGE,
    (value) => value == null || value === '' || isSafe(value),
  );
}

export function yupSafeFormText(yupString) {
  return yupSafeValue(yupString, 'safe-form-text', isSafeFormText);
}

export function yupSafePersonName(yupString) {
  return yupSafeValue(yupString, 'safe-person-name', isSafePersonName);
}

export function yupSafeAddress(yupString) {
  return yupSafeValue(yupString, 'safe-address', isSafeAddress);
}
